// The engine-level parts of the source-paper profile: what is the same for
// every IB History Paper 1 practice (Leah's engine spec, 15 October 2026).
//
// Pure and server-safe, like types.ts: the validator, the MCP and the player
// all read these. The skills pack is the reference build's `TECH` array as it
// stood (spec §10); the markbands are spec §9.6. A practice never carries
// either, so a correction here reaches every practice at once.

import type { SourcePaperRevisionCard, SourcePaperRole } from '../types';

/** The markband text shown under the compare and essay checks (spec §9.6). */
export const SOURCE_PAPER_MARKBANDS: Partial<Record<SourcePaperRole, Array<{ range: string; text: string }>>> = {
  compare: [
    { range: '5–6', text: 'Clear and valid points of comparison and of contrast.' },
    { range: '3–4', text: 'Some valid points of comparison and/or of contrast, which may lack clarity.' },
    { range: '1–2', text: 'Description of the content of the sources rather than valid points of comparison or contrast.' },
  ],
  essay: [
    { range: '7–9', text: 'Focused on the question. Clear references to the sources, used effectively as evidence. Accurate and relevant own knowledge, effectively combined with the sources.' },
    { range: '4–6', text: 'Generally focused. The sources are used as evidence. Own knowledge, where shown, lacks relevance or accuracy, and there is little combining of own knowledge and sources.' },
    { range: '1–3', text: 'Lacks focus. The sources are described rather than used as evidence. Little or no accurate own knowledge.' },
  ],
};

/**
 * The engine's own check patterns (spec §7.5, §7.6, §7.8), as the reference
 * build wrote them. A practice may override the opvl purpose and focus
 * patterns; the rest are about the shape of an answer, not its history, and
 * stay here.
 */
export const SOURCE_PAPER_PATTERNS = {
  opvlValue: 'valu|useful|strength',
  opvlLimit: 'limit|less useful|drawback|weakness|however|but\\b',
  opvlPurpose: 'purpose|justify|justif|defend|explain|persuade|convince|appeal|reputation|audience|aim(ed)? to|intended',
  opvlFocus: 'historian|response',
  similarity: '\\bboth\\b|similar|likewise|also|agree|in common',
  difference: 'whereas|in contrast|however|unlike|while\\b|differ|on the other hand',
  counter: 'however|on the other hand|although|nevertheless|but\\b|in contrast',
  /** Joined with the practice's loaded word to find a thesis and a judgement. */
  judgement: 'extent|largely|partly|limited|mainly|to a (large|great|some|limited)',
} as const;

/** Minimum words for the essay check (spec §7.8). */
export const SOURCE_PAPER_ESSAY_MIN_WORDS = 300;

/**
 * The shared skills pack: ten generic topics used by every practice. They
 * refer to questions by role ("part (a)", "the final question"), never to a
 * practice's letters or numbers (spec §10). Taken from the reference build's
 * `TECH` array word for word.
 */
export const SOURCE_PAPER_SKILLS: SourcePaperRevisionCard[] = [
  {
    title: 'Your hour',
    cues: ['Reading time and the hour', 'The first question', 'The other three questions', 'Time by marks', 'Use reading time well'],
    points: [
      'You get five minutes of reading time, then one hour to write. The paper has four questions on four sources and is marked out of 24.',
      'The first question has two parts: (a) asks what a written source says, for 3 marks, and (b) asks what a visual source suggests, for 2 marks.',
      'The second question asks you to analyse the value and limitations of one source (4 marks). The third asks you to compare and contrast two sources (6 marks). The fourth asks you to use the sources and your own knowledge to answer a question (9 marks).',
      'Spend your time in proportion to the marks: about 5 minutes on part (a), 5 on part (b), 10 on value and limitations, 15 on compare and contrast and 25 on the final question.',
      'Use reading time to read the final question first. Then, as you read each source, you already know what you are looking for.',
    ],
  },
  {
    title: 'Part (a): what the source says',
    cues: ['Marks', 'A frame', 'From the source', 'No own knowledge'],
    points: [
      'One mark for each relevant point, up to 3. Make three separate points.',
      'Use a frame: “The first reason given by the source is that … The second reason given by the source is that … The third reason given by the source is that …”',
      'Take every point from the source. Use brief quotations to show where each point comes from.',
      'Your own knowledge earns nothing here, however accurate it is.',
    ],
  },
  {
    title: 'Part (b): the visual source',
    cues: ['Marks', 'A frame', 'Read the details', 'Message, not description'],
    points: [
      'One mark for each relevant point, up to 2. Give two messages, not a description.',
      'Use a frame: “One message conveyed by the source is that …, because the source shows … A second message conveyed by the source is that …, because the source shows …”',
      'Read the details: labels, symbols, the size and position of figures, the caption, the date and who published it.',
      'Describing what you see is not a message. Say what the detail suggests.',
    ],
  },
  {
    title: 'Value and limitations: origin and purpose',
    cues: ['Origin', 'Purpose', 'Primary or secondary?', 'Use them as evidence'],
    points: [
      'Origin: who made the source, when, where it was published, and what you know about the author that matters. The attribution above the source gives you most of this.',
      'Purpose: why the source exists, who its audience was, and why the author chose this format, such as a speech, a diary, a memoir or a report.',
      'Primary and secondary sources can both be valuable. Neither is more reliable just because of its type.',
      'Use origin, purpose and content as evidence for value and limitations. Do not just list them.',
    ],
  },
  {
    title: 'Value and limitations: making the judgement',
    cues: ['Value', 'Limitation', 'Bias', 'Two traps', 'For 4 marks'],
    points: [
      'Value: given who made it, when and why, what can this source tell this historian? Think about the author’s perspective, the circumstances and any controversy it shows.',
      'Limitation: at what point does the source stop being useful to this historian? What part of the story can it not tell, and how could you check it?',
      'Bias is not automatically a limitation. If you mention it, say who the source favours, against whom, and what that means for the historian. A biased source can be very valuable evidence of a view.',
      'Do not criticise a source for not covering events that happened after it was made, or for leaving something out unless you know it was left out.',
      'For 4 marks: both value and limitations, and at least one reference to each of origin, purpose and content. Only value, or only limitations, can reach 2 at most.',
    ],
  },
  {
    title: 'Source types: written sources',
    cues: ['Start with the type', 'It depends on the question'],
    points: [
      'The type of source suggests where to look for value and limitations. It is a starting point: always tie your point to this source’s origin, purpose and content, and to the historian’s question.',
      'A limitation for one question can be a value for another. A biased speech is weak evidence of what happened, but strong evidence of what its author wanted people to believe.',
    ],
    types: [
      { type: 'Speech', values: 'Shows what a leader wanted an audience to believe, and their public position at a key moment.', limitations: 'Shaped for its audience. It may hide private aims or the real reasons for a policy.' },
      { type: 'Private letter or diary', values: 'Shows private views not meant for publication, often more frank than public statements.', limitations: 'One person’s view, often written in the moment and with limited knowledge of the wider picture.' },
      { type: 'Government document, report or telegram', values: 'Shows the information decision-makers had and official thinking at the time.', limitations: 'Written for officials and may follow the official line. A diplomat’s report of a conversation is second-hand.' },
      { type: 'Memoir or autobiography', values: 'Insider knowledge of decisions, explained by someone who took part.', limitations: 'Written later, with hindsight, and often to defend the author’s reputation. Memory can be selective.' },
      { type: 'Newspaper article', values: 'Shows what the public was told and how events were reported at the time.', limitations: 'Shaped by owners, editors, censorship and readers. It may have been written before the full facts were known.' },
      { type: 'Historian’s work', values: 'Written with hindsight and access to a wide range of evidence, often including archives.', limitations: 'An interpretation, shaped by the evidence available, the historian’s approach and the time it was written.' },
    ],
  },
  {
    title: 'Source types: visual sources and figures',
    cues: ['The same questions', 'Read the attribution'],
    points: [
      'Visual sources need the same questions as written ones: who made it, when, why and for whom.',
      'Read the attribution closely. Where and when a cartoon or photograph was published often tells you more than the image itself.',
    ],
    types: [
      { type: 'Cartoon', values: 'Shows how the cartoonist, and probably the publication’s readers, saw an event. Good evidence of opinion.', limitations: 'Exaggerates for effect and gives one viewpoint. Its meaning depends on context the reader is expected to know.' },
      { type: 'Photograph', values: 'Records a moment, a place or conditions at the time.', limitations: 'The photographer chose what to include and leave out, and some photographs were posed or staged. It shows one moment, not causes.' },
      { type: 'Poster or propaganda', values: 'Shows what a government or group wanted people to think, and the methods it used.', limitations: 'Not evidence of what people actually believed or of what really happened.' },
      { type: 'Statistics', values: 'Show scale and change over time, and allow comparison.', limitations: 'Depend on who collected them, how and why. They may be estimates, incomplete or manipulated.' },
    ],
  },
  {
    title: 'Compare and contrast',
    cues: ['What to look for', 'A frame for similarities', 'A frame for differences', 'Keep them together', 'How many points'],
    points: [
      'Look for what the two sources reveal about the same topic: where they agree and where they differ.',
      'Use a frame for similarities: “Firstly, both sources agree that … The first source says that … and the second source says that …”',
      'Use a frame for differences: “However, whereas the first source suggests that …, the second source suggests that …”',
      'Keep the sources together in every point. Writing about one source and then the other leaves the comparing to the examiner.',
      'Aim for about three similarities and three differences.',
    ],
  },
  {
    title: 'The final question: the source table',
    cues: ['Read it first', 'Draw the table', 'Thirty seconds per source', 'Notes, not sentences', 'Write from the table'],
    points: [
      'In reading time, read the final question first and work out what it is asking.',
      'When writing time starts, draw a table on your rough-work page: three columns (source, evidence that supports, evidence that counters) and a row for each source, plus a row for your own knowledge.',
      'After you answer a question on a source, spend thirty seconds adding one or two notes to its row while it is fresh.',
      'Notes, not sentences. Only note what answers the final question. A point that was right for part (a) may be no use here.',
      'When you reach the final question, your evidence is already sorted into for and against. Write from the table.',
    ],
  },
  {
    title: 'The final question: writing it',
    cues: ['Sections', 'What goes in each', 'Using the sources', 'The conclusion'],
    points: [
      'Plan sections, each giving one interpretation of the question: for example, the case for the claim, the case against it, and a third explanation.',
      'Each section should use the sources, your own knowledge and, where you can, historians’ views.',
      'Use the sources as evidence for your points. Name them in brackets, rather than summarising them one by one.',
      'End with a judgement. Show how far the claim holds, and when or where it holds more or less. Look for loaded words in the question, such as “mainly”, “totally” or “responsible”, and say what they hide.',
    ],
  },
];
